import type { StationMeta } from '@tideprecision/shared';
import { getWindForecastCache, putWindForecast } from '../state/idb.js';
import type { WindForecastCache, WindForecastHour, WindPeriodSummary } from './windTypes.js';

const MET_NORWAY_URL = 'https://api.met.no/weatherapi/locationforecast/2.0/compact';
const CACHE_TTL_MS = 60 * 60 * 1000;
const WIND_SOURCE = 'met-norway';

export interface MetNorwayWindResponse {
  properties?: {
    timeseries?: Array<{
      time?: unknown;
      data?: {
        instant?: {
          details?: {
            wind_speed?: unknown;
            wind_from_direction?: unknown;
          };
        };
      };
    }>;
  };
}

export interface WindDirectionLabel {
  compass: string;
  arrow: string;
}

export interface WindForecastResult {
  forecast: WindForecastCache;
  fromCache: boolean;
  stale: boolean;
}

const COMPASS_16 = [
  '北',
  '北北東',
  '北東',
  '東北東',
  '東',
  '東南東',
  '南東',
  '南南東',
  '南',
  '南南西',
  '南西',
  '西南西',
  '西',
  '西北西',
  '北西',
  '北北西',
] as const;

const ARROWS_8 = ['↑', '↗', '→', '↘', '↓', '↙', '←', '↖'] as const;

export async function loadWindForecast(
  station: StationMeta,
  options: { forceRefresh?: boolean } = {},
): Promise<WindForecastResult> {
  const cached = await getWindForecastCache(station.id);
  const now = Date.now();
  // 取得元が変わった旧キャッシュは新鮮扱いしない（失敗時の stale フォールバックには使う）
  const fresh = cached && cached.source === WIND_SOURCE && now - cached.fetchedAt < CACHE_TTL_MS;

  if (fresh && !options.forceRefresh) {
    return { forecast: cached, fromCache: true, stale: false };
  }

  if (!canUseNetwork() && cached) {
    return { forecast: cached, fromCache: true, stale: true };
  }

  try {
    const forecast = await fetchWindForecast(station);
    await putWindForecast(forecast);
    return { forecast, fromCache: false, stale: false };
  } catch (e) {
    if (cached) {
      return { forecast: cached, fromCache: true, stale: true };
    }
    throw e;
  }
}

export async function fetchWindForecast(station: StationMeta): Promise<WindForecastCache> {
  const url = buildMetNorwayUrl(station);
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`MET Norway API ${res.status}`);
  }

  const body = (await res.json()) as MetNorwayWindResponse;
  const hourly = parseMetNorwayHourly(body);
  if (hourly.length === 0) {
    throw new Error('風予報データが空です');
  }

  return {
    stationId: station.id,
    stationName: station.name,
    latitude: station.latitude,
    longitude: station.longitude,
    source: WIND_SOURCE,
    timezone: 'Asia/Tokyo',
    fetchedAt: Date.now(),
    hourly,
  };
}

export function filterWindHoursByDate(
  forecast: WindForecastCache,
  date: string,
): WindForecastHour[] {
  return forecast.hourly.filter((h) => h.time.startsWith(`${date}T`));
}

/// 予報行の間隔に1時間超の飛びがあるか（6時間刻みを含む日の判定）。
/// 当日は予報が現在時刻から始まるため行数が少なくても1時間刻みなら false。
export function hasSparseIntervals(hours: WindForecastHour[]): boolean {
  const minutes = hours.map(
    (h) => Number(h.time.slice(11, 13)) * 60 + Number(h.time.slice(14, 16)),
  );
  for (let i = 1; i < minutes.length; i++) {
    if (minutes[i]! - minutes[i - 1]! > 60) return true;
  }
  return false;
}

export function findWindHourByMinute(
  hours: WindForecastHour[],
  minute: number,
): WindForecastHour | null {
  if (hours.length === 0) return null;
  // 6時間刻みの日もあるため、最も近い予報時刻を返す（前後3時間を超えたら無し）
  let best: WindForecastHour | null = null;
  let bestDiff = Number.POSITIVE_INFINITY;
  for (const h of hours) {
    const rowMinute = Number(h.time.slice(11, 13)) * 60 + Number(h.time.slice(14, 16));
    const diff = Math.abs(rowMinute - minute);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = h;
    }
  }
  return bestDiff <= 180 ? best : null;
}

export function summarizeWindByPeriods(hours: WindForecastHour[]): WindPeriodSummary[] {
  const periods = [
    { start: 0, end: 5, label: '0-5時' },
    { start: 6, end: 11, label: '6-11時' },
    { start: 12, end: 17, label: '12-17時' },
    { start: 18, end: 23, label: '18-23時' },
  ];

  return periods.flatMap((period) => {
    const inPeriod = hours.filter((h) => {
      const hour = Number(h.time.slice(11, 13));
      return hour >= period.start && hour <= period.end;
    });
    if (inPeriod.length === 0) return [];

    let speedSum = 0;
    let sinSum = 0;
    let cosSum = 0;
    for (const h of inPeriod) {
      speedSum += h.windSpeedMs;
      const rad = (h.windDirectionDeg * Math.PI) / 180;
      sinSum += Math.sin(rad);
      cosSum += Math.cos(rad);
    }

    const direction = normalizeDegrees((Math.atan2(sinSum, cosSum) * 180) / Math.PI);
    return [{
      label: period.label,
      windSpeedMs: speedSum / inPeriod.length,
      windDirectionDeg: direction,
    }];
  });
}

export function formatWindDirection(degrees: number): WindDirectionLabel {
  const normalized = normalizeDegrees(degrees);
  const compassIndex = Math.round(normalized / 22.5) % 16;
  // 風向は「吹いてくる向き」。矢印は実際に吹いていく向きを示す。
  const toBearing = normalizeDegrees(normalized + 180);
  const arrowIndex = Math.round(toBearing / 45) % 8;
  return {
    compass: COMPASS_16[compassIndex]!,
    arrow: ARROWS_8[arrowIndex]!,
  };
}

export function formatFetchedAt(epochMs: number): string {
  const d = new Date(epochMs + 9 * 60 * 60 * 1000);
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mi = String(d.getUTCMinutes()).padStart(2, '0');
  return `${mm}/${dd} ${hh}:${mi}`;
}

export function windSpeedTextClass(speed: number): string {
  if (speed >= 10) return 'text-rose-300';
  if (speed >= 7) return 'text-orange-300';
  if (speed >= 4) return 'text-amber-300';
  if (speed >= 2) return 'text-sky-300';
  return 'text-emerald-300';
}

function buildMetNorwayUrl(station: StationMeta): string {
  const url = new URL(MET_NORWAY_URL);
  // MET Norway の利用条件により座標は小数4桁まで
  url.searchParams.set('lat', station.latitude.toFixed(4));
  url.searchParams.set('lon', station.longitude.toFixed(4));
  return url.toString();
}

export function parseMetNorwayHourly(body: MetNorwayWindResponse): WindForecastHour[] {
  const timeseries = body.properties?.timeseries;
  if (!Array.isArray(timeseries)) return [];

  const rows: WindForecastHour[] = [];
  for (const entry of timeseries) {
    const details = entry?.data?.instant?.details;
    const windSpeedMs = details?.wind_speed;
    const windDirectionDeg = details?.wind_from_direction;
    if (typeof entry?.time !== 'string') continue;
    if (typeof windSpeedMs !== 'number' || !Number.isFinite(windSpeedMs)) continue;
    if (typeof windDirectionDeg !== 'number' || !Number.isFinite(windDirectionDeg)) continue;
    const time = toJstLocalTime(entry.time);
    if (time === null) continue;
    rows.push({
      time,
      windSpeedMs,
      windDirectionDeg: normalizeDegrees(windDirectionDeg),
    });
  }
  return rows;
}

/// MET Norway の UTC 時刻（ISO 8601）を JST の "YYYY-MM-DDTHH:mm" に変換する。
function toJstLocalTime(isoUtc: string): string | null {
  const utc = new Date(isoUtc);
  if (Number.isNaN(utc.getTime())) return null;
  const jst = new Date(utc.getTime() + 9 * 60 * 60 * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${jst.getUTCFullYear()}-${pad(jst.getUTCMonth() + 1)}-${pad(jst.getUTCDate())}T${pad(jst.getUTCHours())}:${pad(jst.getUTCMinutes())}`;
}

function normalizeDegrees(value: number): number {
  return ((value % 360) + 360) % 360;
}

function canUseNetwork(): boolean {
  if (typeof navigator === 'undefined') return true;
  return navigator.onLine;
}
